import { useManager } from 'dreamstate';
import { FocusEvent, LegacyRef, useCallback, useState, ReactElement } from 'react';

import {
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  FormControl,
  FormLabel,
  Switch,
  rem,
} from '@vimeo/bokeh';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';

import { useControlledPopover } from '../../../hooks/dom/useControlledPopover';
import { ChatManager, IChatContext } from '../../../store/firebase/interaction/chat';
import {
  trackDisableAudienceChat,
  trackEnableAudienceChat,
  trackExportChat,
} from '../../../tracking/tracking_engagement_chat';
import { T_AUDIENCE_CHAT, T_DOWNLOAD, T_SETTINGS } from '../../../translations';
import { createDomName } from '../../../utils/dom/identifying';
import { BokehTooltip } from '../../bokeh';
import { LiveErrorBoundary } from '../../error/boundary';
import { DownloadModal } from '../shared/DownloadModal';
import { InteractionToolsSubmenu } from '../shared/InteractionToolsSubmenu';

interface IChatSettingsButtonProps extends IBaseDomElementProps {
  withInteractionSubmenu?: boolean;
  buttonIcon: ReactElement;
  chatContext?: IChatContext;
}

export function ChatSettingsButton({
  id = createDomName('chat-settings-button'),
  className = createDomName('chat-settings-button'),
  withInteractionSubmenu = false,
  buttonIcon,
  chatContext: {
    chatSessionsHistory,
    isEnabled,
    chatActions: { enableChat, disableChat },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(ChatManager),
}: IChatSettingsButtonProps) {
  const [isDownloadOpen, setIsDownloadOpen] = useState<boolean>(false);

  const { triggerRef, contentRef, isOpen, onOpenPopover, onClosePopover } = useControlledPopover();

  const onChangeChatState = useCallback(() => {
    if (isEnabled) {
      trackDisableAudienceChat();
      disableChat();
    } else {
      trackEnableAudienceChat();
      enableChat();
    }
  }, [isEnabled, enableChat, disableChat]);

  const onOpenMenu = useCallback(() => {
    onOpenPopover();
  }, [onOpenPopover]);

  const onClickOpenDownloadModal = useCallback(() => {
    setIsDownloadOpen(true);
    onClosePopover();
  }, [onClosePopover]);

  const onClickCloseDownloadModal = useCallback(() => setIsDownloadOpen(false), []);

  const onClickDownload = useCallback(() => {
    trackExportChat();
    setIsDownloadOpen(false);
  }, []);

  const onFocus = useCallback((event: FocusEvent<HTMLButtonElement>) => {
    event.preventDefault();
  }, []);

  return (
    <LiveErrorBoundary component={'ChatSettingsButton'} isDetailed={false}>
      <Menu
        isOpen={isOpen}
        placement={'bottom'}
        closeOnSelect={false}
        onOpen={onOpenMenu}
        onClose={onClosePopover}
      >
        <BokehTooltip label={isOpen ? null : T_SETTINGS} placement={'bottom'}>
          <MenuButton
            as={IconButton}
            ref={triggerRef}
            id={id}
            className={className}
            aria-label={'settings'}
            icon={buttonIcon}
            size={'sm'}
            variant={'minimal'}
            onFocus={onFocus}
          />
        </BokehTooltip>

        <MenuList
          ref={contentRef as LegacyRef<HTMLDivElement>}
          overflow={'unset'}
          width={rem(240)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: rem(4),
          }}
        >
          <MenuItem>
            <FormControl
              display={'flex'}
              alignItems={'center'}
              justifyContent={'space-between'}
              width={'100%'}
            >
              <FormLabel htmlFor={'audience-chat'} marginBottom={0}>
                {T_AUDIENCE_CHAT}
              </FormLabel>

              <Switch
                id={'audience-chat'}
                size={'sm'}
                isChecked={Boolean(isEnabled)}
                onChange={onChangeChatState}
              />
            </FormControl>
          </MenuItem>

          <MenuItem onClick={onClickOpenDownloadModal}>{T_DOWNLOAD}</MenuItem>

          {withInteractionSubmenu ? (
            <>
              <InteractionToolsSubmenu
                placement={'left-start'}
                onCloseParentMenu={onClosePopover}
              />
            </>
          ) : null}
        </MenuList>
      </Menu>

      <DownloadModal
        header={T_DOWNLOAD}
        prefix={'export_chat'}
        history={chatSessionsHistory}
        isOpen={isDownloadOpen}
        onClose={onClickCloseDownloadModal}
        onDownload={onClickDownload}
      />
    </LiveErrorBoundary>
  );
}
